/**
 * AI Configuration Service
 * Manages per-tenant AI provider key + org profile (admin only).
 * The decrypted key NEVER leaves the backend — GET returns only `keyLastFour`.
 */

import apiClient from './api';
import type { ApiResponse } from '../types';

export type AIProviderName = 'anthropic' | 'openai';

export interface ProviderModelInfo {
  id: string;
  label: string;
  contextWindow: number;
}

export interface AIConfigView {
  provider: AIProviderName | null;
  model: string | null;
  enabled: boolean;
  hasKey: boolean;
  keyLastFour: string | null;
  dailyTokenCap: number | null;
  updatedAt: string | null;
  supportedModels: Record<AIProviderName, ProviderModelInfo[]>;
}

export interface UpdateAIConfigInput {
  provider?: AIProviderName;
  apiKey?: string;
  model?: string;
  enabled?: boolean;
  dailyTokenCap?: number;
}

export interface OrgProfileInput {
  industry?: string | null;
  businessModel?: string | null;
  companyDescription?: string | null;
}

export interface TestConnectionResult {
  ok: true;
  provider: AIProviderName;
  model: string;
  latencyMs: number;
}

export const aiConfigService = {
  get: async (): Promise<AIConfigView> => {
    const r = await apiClient.get<ApiResponse<{ aiConfig: AIConfigView }>>('/organization/ai-config');
    if (r.data.success && r.data.data) return r.data.data.aiConfig;
    throw new Error(r.data.message || 'Failed to load AI configuration');
  },

  update: async (input: UpdateAIConfigInput): Promise<AIConfigView> => {
    const r = await apiClient.patch<ApiResponse<{ aiConfig: AIConfigView }>>(
      '/organization/ai-config',
      input
    );
    if (r.data.success && r.data.data) return r.data.data.aiConfig;
    throw new Error(r.data.message || 'Failed to update AI configuration');
  },

  remove: async (): Promise<void> => {
    const r = await apiClient.delete<ApiResponse>('/organization/ai-config');
    if (!r.data.success) throw new Error(r.data.message || 'Failed to remove AI configuration');
  },

  test: async (
    input?: { provider?: AIProviderName; apiKey?: string; model?: string }
  ): Promise<TestConnectionResult> => {
    const r = await apiClient.post<ApiResponse<TestConnectionResult>>(
      '/organization/ai-config/test',
      input || {}
    );
    if (r.data.success && r.data.data) return r.data.data;
    throw new Error(r.data.message || 'Connection test failed');
  },

  updateOrgProfile: async (input: OrgProfileInput): Promise<{
    industry: string | null;
    businessModel: string | null;
    companyDescription: string | null;
  }> => {
    const r = await apiClient.patch<
      ApiResponse<{ profile: { industry: string | null; businessModel: string | null; companyDescription: string | null } }>
    >('/organization/profile', input);
    if (r.data.success && r.data.data) return r.data.data.profile;
    throw new Error(r.data.message || 'Failed to update organization profile');
  },
};
